import { useQuery, useQueries, useMutation } from "@tanstack/react-query";
import { queryClient } from "src/lib/react-query/queryClient";
import useAppStore from "src/stores/appStore";
import { useEffect } from "react";
import { FETCH_QUERY_KEYS } from "src/queries/consts";
import collectionsService from "src/queries/services/collections";
import { CACHE_DURATION } from "@consts";
import { selectedCollectionSelector, collectionNameSelector } from "src/stores/selectors/collectionsSelectors";
import { selectedAssistantSelector, pageNameSelector, systemModeSelector } from "src/stores/selectors/appSelectors";
import { SYSTEM_MODES } from "src/stores/slices/createAppSlice";

export const useMutateCollection = () => useMutation({
  mutationFn: collectionsService.updateCollection
});

export const useFetchCollections = (params) => {
  const queryFn = async () => {
    useAppStore.getState().setPageLoading(true);
    const collections = await collectionsService.fetchCollections(params);
    return collections;
  };

  const { isLoading, data, error, isError } = useQuery({
    queryKey: [FETCH_QUERY_KEYS.COLLECTIONS, params.selectedAssistantId, params.collectionName],
    queryFn,
    enabled: !!params.selectedAssistantId && !!params.collectionName,
    staleTime: 0,
    gcTime: 0
  });

  if (isError) {
    useAppStore.getState().setPageError("Failed to fetch all collections", error);
    useAppStore.getState().setPageLoading(false);
  }

  useEffect(() => {
    if (data) {
      useAppStore.getState().setCollections(data);
    }
  }, [data]);

  return { isLoading };
};

export const useFetchCollection = (params) => {
  const queryFn = async () => {
    useAppStore.getState().setPageLoading(true);
    useAppStore.getState().setCollectionLoading(true);
    return await collectionsService.fetchCollection(params);
  };

  const { isLoading, data, error, isError } = useQuery({
    queryKey: [FETCH_QUERY_KEYS.COLLECTION, params.selectedAssistantId, params.collectionName, params.collectionKey, params.collectionInstanceKey],
    queryFn,
    enabled: !!params.selectedAssistantId && !!params.collectionName && !!params.collectionKey && !!params.collectionInstanceKey,
    staleTime: CACHE_DURATION.ONE_MINUTE,
    refetchOnWindowFocus: false
  });

  if (isError) {
    useAppStore.getState().setPageLoading(false);

    if (!data) {
      useAppStore.getState().setPageError("Failed to fetch a single collection", error);
    }
  }

  useEffect(() => {
    if (data) {
      const { collectionConfigChanged, policiesConfigChanged } = useAppStore.getState();
      const hasUnsavedChanges = collectionConfigChanged || policiesConfigChanged;

      if (!hasUnsavedChanges) {
        useAppStore.getState().setCollection(data, params.collectionInstanceKey);
      }
    }
  }, [data]);

  return { isLoading };
};

export const useFetchCollectionRefs = (params) => {
  const systemMode = useAppStore(systemModeSelector);

  const uniqueRefCollections = Array.from(
    new Map(
      params?.refCollections?.map((item) => [
        `${item.collection_id}:${JSON.stringify(item.query_params)}`,
        item
      ])
    ).values()
  );

  const queries = uniqueRefCollections.map(({ collection_id, query_params }) => ({
    queryKey: [FETCH_QUERY_KEYS.COLLECTION_REFS, collection_id, query_params],
    queryFn: async () => {
      const fetchParams = {
        selectedAssistantId: params.selectedAssistantId,
        collectionId: collection_id,
        params: query_params
      };
      return await collectionsService.fetchCollectionRefs(fetchParams);
    },
    select: (data) => {
      const normalizedData = {
        draft: data.draft.filter(({ display_key }) => display_key !== ""),
        live: data.live.filter(({ display_key }) => display_key !== "")
      };
      if (systemMode === SYSTEM_MODES.READ_ONLY || !params.hasDraft) {
        return normalizedData.live;
      }
      return normalizedData.draft;
    },
    enabled: !!params.selectedAssistantId && !!params?.refCollections?.length,
    staleTime: CACHE_DURATION.ONE_MINUTE
  }));

  const queryResults = useQueries({ queries });
  const allOptions = params?.refCollections?.reduce((acc, { key, collection_id, query_params }) => {
    const index = uniqueRefCollections.findIndex((item) =>
      `${item.collection_id}:${JSON.stringify(item.query_params)}` === `${collection_id}:${JSON.stringify(query_params)}`);
    acc[key] = queryResults[index]?.data || [];
    return acc;
  }, {});
  const isLoading = queryResults.some((result) => result.isPending || result.isFetching);
  const isError = queryResults.some((result) => result.isError);

  return { isLoading, data: allOptions, isError };
};

export const useFetchExpressionVariables = ({ selectedAssistantId }) => {
  const systemMode = useAppStore(systemModeSelector);
  const { data, isLoading, isError } = useQuery({
    queryKey: [FETCH_QUERY_KEYS.EXPRESSION_VARIABLES, selectedAssistantId],
    queryFn: async () =>
      await collectionsService.fetchCollectionRefs({
        selectedAssistantId,
        collectionId: "conversation-variables",
        params: { expression_use: true }
      }),
    enabled: !!selectedAssistantId,
    staleTime: 0,
    gcTime: 0
  });
  const variables = data
    ? (systemMode === SYSTEM_MODES.READ_ONLY ? data.live : (data.draft.length ? data.draft : data.live))
      .filter(({ display_key }) => Boolean(display_key))
    : [];
  return { variables, isLoading, isError };
};

export const useRevealSecret = (integrationEnv, secretId) => {
  const selectedAssistant = useAppStore(selectedAssistantSelector);
  const selectedCollection = useAppStore(selectedCollectionSelector);
  const collectionName = useAppStore(collectionNameSelector);
  const pageName = useAppStore(pageNameSelector);
  const systemMode = useAppStore(systemModeSelector);

  const collectionKey = selectedCollection?.configuration?.key;
  const collectionInstanceKey = selectedCollection?.key;

  return useMutation({
    mutationFn: async () => await collectionsService.revealSecret({
      selectedAssistant,
      collectionName,
      collectionKey,
      collectionInstanceKey,
      integrationEnv,
      secretId,
      pageName,
      systemMode
    })
  });
};

export const useDuplicateCollection = () => {
  const selectedAssistantId = useAppStore(selectedAssistantSelector)?.assistantId;
  const collectionName = useAppStore(collectionNameSelector);

  return useMutation({
    mutationFn: async (params) => await collectionsService.duplicateCollection({
      selectedAssistantId,
      collectionName,
      skillTypeKey: params.skillTypeKey,
      skillInstanceKey: params.skillInstanceKey,
      body: {
        newConfigId: params.newConfigId,
        displayKey: params.displayKey,
        description: params.description
      }
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [FETCH_QUERY_KEYS.VALIDATION_RESULTS] });
    }
  });
};

export const useTestExpression = () => {
  const selectedAssistantId = useAppStore(selectedAssistantSelector)?.assistantId;
  const collectionName = useAppStore(collectionNameSelector);
  const collectionKey = useAppStore(selectedCollectionSelector)?.configuration?.key;
  const collectionInstanceKey = useAppStore(selectedCollectionSelector)?.key;
  const mode = useAppStore(systemModeSelector);

  return useMutation({
    mutationFn: async (body) => await collectionsService.testExpression({ selectedAssistantId, collectionName, collectionKey, collectionInstanceKey, mode, ...body })
  });
};

export const useTestUsedExpression = () => {
  const selectedAssistantId = useAppStore(selectedAssistantSelector)?.assistantId;
  const collectionName = useAppStore(collectionNameSelector);
  const selectedCollection = useAppStore(selectedCollectionSelector);
  const mode = useAppStore(systemModeSelector);

  const collectionKey = selectedCollection?.configuration?.key;

  return useMutation({
    mutationFn: async (body) => await collectionsService.testUsedExpression({
      selectedAssistantId,
      collectionName,
      collectionKey,
      mode,
      ...body
    })
  });
};

export default {
  useMutateCollection,
  useFetchCollections,
  useFetchCollection,
  useFetchCollectionRefs,
  useFetchExpressionVariables,
  useRevealSecret,
  useDuplicateCollection
};
